import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

// Define available background colors
export type BackgroundColor =
  | "blue"
  | "green"
  | "purple"
  | "red"
  | "orange"
  | "yellow";

type BackgroundContextType = {
  currentBg: BackgroundColor;
  changeBg: (color: BackgroundColor) => void;
  getBgClasses: () => string;
  getDeepBgClasses: () => string;
};

// Create the context
const BackgroundContext = createContext<BackgroundContextType | undefined>(
  undefined
);

// Background color mapping
const bgClasses: Record<BackgroundColor, string> = {
  blue: "bg-blue-50",
  green: "bg-green-50",
  purple: "bg-purple-50",
  red: "bg-red-50",
  orange: "bg-orange-50",
  yellow: "bg-yellow-50",
};

// Deeper background colors for more contrast
const deepBgClasses: Record<BackgroundColor, string> = {
  blue: "bg-blue-600",
  green: "bg-green-600",
  purple: "bg-purple-600",
  red: "bg-red-600",
  orange: "bg-orange-600",
  yellow: "bg-yellow-600",
};

// Define the provider component
export const BackgroundProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Get the saved color from localStorage or use the default
  const [currentBg, setCurrentBg] = useState<BackgroundColor>(() => {
    try {
      const savedBg = localStorage.getItem("app-bg-color");
      // Validate that the saved value is one of the allowed colors
      const validColors: BackgroundColor[] = [
        "blue",
        "green",
        "purple",
        "red",
        "orange",
        "yellow",
      ];
      return savedBg && validColors.includes(savedBg as BackgroundColor)
        ? (savedBg as BackgroundColor)
        : "blue";
    } catch (error) {
      
      return "blue";
    }
  });

  // Change the background color
  const changeBg = (color: BackgroundColor) => {
    setCurrentBg(color);
    try {
      localStorage.setItem("app-bg-color", color);
    } catch (error) {
      
    }
  };

  // Get the CSS classes for the current background
  const getBgClasses = () => {
    return bgClasses[currentBg];
  };

  // Get the deeper CSS classes for the current background
  const getDeepBgClasses = () => {
    return deepBgClasses[currentBg];
  };

  // Apply the background color to the body on load and when changed
  useEffect(() => {
    // Remove all possible background classes
    document.body.classList.remove(...Object.values(bgClasses));

    // Add the current background class
    document.body.classList.add(bgClasses[currentBg]);

    // Set a data attribute that can be used by CSS selectors
    document.body.setAttribute("data-bg-color", currentBg);

  }, [currentBg]);

  return (
    <BackgroundContext.Provider
      value={{ currentBg, changeBg, getBgClasses, getDeepBgClasses }}
    >
      {children}
    </BackgroundContext.Provider>
  );
};

// Custom hook to use the background context
// eslint-disable-next-line react-refresh/only-export-components
export const useBackground = (): BackgroundContextType => {
  const context = useContext(BackgroundContext);
  if (context === undefined) {
    throw new Error("useBackground must be used within a BackgroundProvider");
  }
  return context;
};
